import { useCallback, useEffect, useState } from "react";
import { connect } from "react-redux";
import { useIntl } from "react-intl";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { TabMenu } from "primereact/tabmenu";
import { useMountEffect, usePrevious } from "primereact/hooks";
import { ConfirmDialog, confirmDialog } from "primereact/confirmdialog";
import CustomBreadCrumbs from "../../../common/breadcrumbs";
import Form from "../../../common/form";
import {
  resetUpload,
  uploadRequest,
} from "../../../../store/slices/uploadSlice";
import {
  createRequest,
  detailsRequest,
  updateDetailRequest,
  assignmentsRequest,
  addAssignmentRequest,
  deleteAssignmentRequest,
} from "../../../../store/slices/learningPathwaySlice";
import { listRequest as certificateListRequest } from "../../../../store/slices/certificateSlice";
import { listRequest as rewardListRequest } from "../../../../store/slices/rewardSlice";
import { listRequest as audienceListRequest } from "../../../../store/slices/audienceSlice";
import { listRequest as milestoneListRequest } from "../../../../store/slices/learningMilestoneSlice";
import { learningProgramRequest } from "../../../../store/slices/metaSlice";
import Section from "../../../common/section";
import FileUpload from "../../../common/fileUpload";
import * as RoleHelper from "../../../../helpers/RoleHelper";
import TooltipButton from "../../../common/tooltipButton";
import { AccessScopeConstants, GlobalConstants } from "../../../../constants";
import LearningPathwayDetailsForm from "./LearningPathwayDetailsForm";
import List from "../../../common/list";
import { Dialog } from "primereact/dialog";
import AssignmentDetailsForm from "./AssignmentDetailsForm";
import * as ListCellRenders from "../../../common/list/ListCellRenders";
import { buildPathwayAssignmentPayload } from "../../../../helpers/AssignmentPayload";
import {
  PICKER_PAGE_SIZE,
  useServerSearch,
} from "../../../common/hooks/useServerSearch";

const LearningPathwayDetails = ({
  action,
  assignmentsRequest,
  audienceListRequest,
  audiences,
  createRequest,
  detailsRequest,
  error,
  labels,
  learningMilestones,
  learningProgramRequest,
  menu,
  milestoneListRequest,
  progress,
  record,
  requesting,
  resetUpload,
  updateDetailRequest,
  uploadDetails,
  uploading,
  uploadRequest,
  user,
  addAssignmentRequest,
  progarms,
  additionalProps = {},
  certificateListRequest,
  certificates,
  deleteAssignmentRequest,
  rewardListRequest,
  rewards,
}) => {
  const { isFromAssigment = false } = additionalProps;

  const prevProps = usePrevious({
    requesting,
  });

  const params = useParams();
  const [bannerFileName, setBannerFileName] = useState("");
  const navigate = useNavigate();
  const location = useLocation();
  const intl = useIntl();
  const [isEditable, setIsEditable] = useState(params?.id ? false : true);
  const [activeTab, setActiveTab] = useState(isFromAssigment ? 2 : 0);
  const [isAssignmentDialogVisible, setIsAssignmentDialogVisible] =
    useState(false);

  const [isRecordClone, setIsRecordClone] = useState(false);

  const { role } = user || {};
  const canUpdate = RoleHelper.checkRoleScope(
    role?.scope,
    AccessScopeConstants.ContentAccess,
    AccessScopeConstants.UpdateScope
  );

  const canDelete = RoleHelper.checkRoleScope(
    role?.scope,
    AccessScopeConstants.ContentAccess,
    AccessScopeConstants.DeleteScope
  );

  // The reward, milestone and cohort pickers: the first page by name, then the
  // server's matches for what the admin types (E2E25)
  const pickerQuery = (namePattern = "") => ({
    pageSize: PICKER_PAGE_SIZE,
    pageNo: 1,
    sortBy: "asc",
    sortColumn: "name",
    namePattern,
  });
  const loadRewards = (namePattern) =>
    rewardListRequest({ IsActive: true, ...pickerQuery(namePattern) });
  // Milestones for the Basic Info tab's milestone picker
  const loadMilestones = (namePattern) =>
    milestoneListRequest(pickerQuery(namePattern));
  const loadAudiences = (namePattern) =>
    audienceListRequest(pickerQuery(namePattern));
  const searchRewards = useServerSearch(loadRewards);
  const searchMilestones = useServerSearch(loadMilestones);
  const searchAudiences = useServerSearch(loadAudiences);

  useMountEffect(() => {
    resetUpload();
    certificateListRequest();
    loadRewards();
    loadMilestones();
    // Every program, for the "Completion of a Learning Program" assignment
    // (the same dropdown list the audience screen uses)
    learningProgramRequest();
    loadAudiences();

    const searchParams = new URLSearchParams(location?.search);
    const cloneId = searchParams.get("clone");
    if (cloneId) {
      setIsRecordClone(true);
      detailsRequest({ id: cloneId });
    }
  });

  useEffect(() => {
    if (params?.id) {
      detailsRequest({ id: params.id });
    }
  }, [params?.id, detailsRequest]);

  useEffect(() => {
    if (params?.id && activeTab === 2) {
      assignmentsRequest({ id: params.id });
    }
  }, [activeTab]);

  useEffect(() => {
    if (
      prevProps?.requesting &&
      (action === "create" || action === "update") &&
      !requesting &&
      !error
    ) {
      navigate(menu.to);
    }

    if (
      prevProps?.requesting &&
      (action === "assignment-create" || action === "assignment-delete") &&
      !requesting &&
      !error
    ) {
      assignmentsRequest({ id: params.id });
    }
  }, [error, action, requesting, prevProps?.requesting]);

  const onBannerUpload = (event) => {
    const file = event.files[0];
    setBannerFileName(file.name);
    uploadRequest({ file, type: "image" });
  };

  const onDeleteAction = (data) => {
    confirmDialog({
      message: intl.formatMessage(
        { id: "delete_object_message" },
        {
          name: (
            <>
              <span>
                {intl.formatMessage({
                  id: data?.subjectTypeName || "-",
                })}
              </span>
              <span>
                {" - "}
                {data?.subjectName}
              </span>
            </>
          ),
          object: intl.formatMessage({ id: "assignment" }),
        }
      ),
      header: intl.formatMessage({ id: "delete_confirmation" }),
      icon: "pi pi-info-circle",
      defaultFocus: "reject",
      acceptClassName: "p-button-danger",
      rejectClassName: "p-button-secondary",
      accept: () => {
        deleteAssignmentRequest({ id: data.id });
      },
      reject: () => {},
    });
  };

  const onSubmit = (values) => {
    const payload = {
      ...values,
      isCertified: false,
      certificateId:
        values?.certificateId === "no-cert"
          ? null
          : values.certificateId || null,
      name: values?.name,
      tags: values?.tags,
      bannerImageId:
        uploadDetails?.[bannerFileName]?.artifactId ||
        record?.bannerImageId ||
        null,
      milestones: values?.milestones?.map((item, index) => ({
        operator: item?.operator?.id,
        learningMilestoneId: item?.learningMilestone?.id,
        order: index + 1,
      })),
      type: values?.type.value || values?.type,
      isRepetable: values?.type === 1 ? values?.isRepetable || false : false,
      repetableInMonths:
        values?.type === 1 && values?.isRepetable
          ? values?.repetableInMonths
          : 0,
      // Convert 'no_reward' back to 0 for API
      rewardTriggerType:
        values.rewardTriggerType === "no_reward" ? 0 : values.rewardTriggerType,
    };
    if (params?.id) {
      delete payload.milestones;
      updateDetailRequest({ id: params.id, payload });
    } else {
      createRequest(payload);
    }
  };

  const handleAddAsignment = (values) => {
    addAssignmentRequest({
      id: record?.id,
      payload: buildPathwayAssignmentPayload(values),
    });
  };

  return (
    <div className="pb-8 md:pb-0">
      <CustomBreadCrumbs
        labels={labels}
        navigate={navigate}
        editLabel={record?.name}
      />
      <div className="edexlms-page-content">
        <div className="edexlms-page-header">
          <div className="edexlms-page-title">
            {intl.formatMessage({
              id: params?.id
                ? isFromAssigment
                  ? "learning_program_details"
                  : "edit_learning_program"
                : "add_learning_program",
            })}
          </div>
          {(params?.id &&
            !isFromAssigment &&
            !isEditable &&
            activeTab === 0 && (
              <div className="edexlms-page-actions">
                <TooltipButton
                  icon="pi pi-pencil"
                  outlined
                  onClick={() =>
                    (record?.id && setIsEditable(!isEditable)) || null
                  }
                  label={intl.formatMessage({ id: "edit" })}
                  type="button"
                  size="small"
                  disabled={!canUpdate || !record?.id}
                  tooltipOptions={{ showOnDisabled: true, position: "bottom" }}
                  tooltip={
                    canUpdate && !record?.isSystemDefined
                      ? ""
                      : intl.formatMessage({ id: "no_permission" })
                  }
                />
              </div>
            )) ||
            null}
          {(params?.id && activeTab === 2 && (
            <div className="edexlms-page-actions">
              <TooltipButton
                icon="pi pi-plus"
                outlined
                onClick={() =>
                  (record?.id && setIsAssignmentDialogVisible(true)) || null
                }
                label={intl.formatMessage({ id: "add_assignment" })}
                type="button"
                size="small"
                disabled={!canUpdate || !record?.id}
                tooltipOptions={{ showOnDisabled: true, position: "bottom" }}
                tooltip={
                  canUpdate && !record?.isSystemDefined
                    ? ""
                    : intl.formatMessage({ id: "no_permission" })
                }
              />
            </div>
          )) ||
            null}
        </div>
        <div className="edexlms-page-body learning-milestones">
          {(params?.id && (
            <TabMenu
              activeIndex={activeTab}
              className="edexlms-tab-menu"
              model={[
                {
                  label: intl.formatMessage({ id: "basic_info" }),
                  command: () => {
                    if (activeTab !== 0) {
                      setActiveTab(0);
                      setIsEditable(false);
                    }
                  },
                },
                {
                  label: intl.formatMessage({ id: "learning_milestones" }),
                  command: (e) => {
                    if (activeTab !== 1) {
                      setActiveTab(1);
                      setIsEditable(false);
                    }
                  },
                },
                {
                  label: intl.formatMessage({ id: "assignments" }),
                  command: (e) => {
                    if (activeTab !== 2) {
                      setActiveTab(2);
                      setIsEditable(false);
                    }
                  },
                },
              ]}
            />
          )) ||
            null}
          {(activeTab === 0 && (
            <Form
              initialValues={{
                certificateId: record?.certificateId || "no-cert",
                name: (record && !isRecordClone && record?.name) || "",
                tags: record?.tags,
                isCertified: record?.isCertified || false,
                isLinear: record?.isLinear || false,
                type: record?.type || null,
                isRepetable: record?.isRepetable || false,
                repetableInMonths: record?.repetableInMonths || 0,
                rewardTriggerType:
                  record?.rewardTriggerType !== undefined
                    ? !record?.rewardTriggerType
                      ? "no_reward"
                      : record?.rewardTriggerType
                    : "no_reward",
                rewardId: record?.rewardId,
                pointsOnCompletion: record?.pointsOnCompletion || 0,
                milestones: ((!record || !isRecordClone) && [
                  {
                    operator: GlobalConstants.LEARNING_OPERATORS.filter(
                      (op) => op.default
                    ).map((item) => ({
                      name: intl.formatMessage({ id: item.name }),
                      id: item.id,
                    }))[0],
                  },
                ]) || [
                  ...record?.milestones.map((item) => ({
                    operator: GlobalConstants.LEARNING_OPERATORS.filter(
                      (op) =>
                        op.name.toLocaleLowerCase() ===
                        item.operator.toLocaleLowerCase()
                    ).map((op) => ({
                      name: intl.formatMessage({ id: op.name }),
                      id: op.id,
                    }))[0],
                    // From the program itself: the milestone may not be in
                    // the picker's current page
                    learningMilestone: {
                      name: `${item.learningMilestoneName}`,
                      id: item.learningMilestoneId,
                    },
                  })),
                ],
              }}
              disabled={!isEditable}
              submitBtn={{
                label: intl.formatMessage({
                  id: params?.id
                    ? "update_learning_program"
                    : "add_learning_program",
                }),
                onClick: onSubmit,
              }}
              cancelBtn={{
                label: intl.formatMessage({ id: "cancel" }),
                onClick: () => navigate(menu?.to),
              }}
            >
              <div className="flex flex-column gap-4">
                <LearningPathwayDetailsForm
                  learningMilestones={learningMilestones}
                  onMilestoneSearch={searchMilestones}
                  onRewardSearch={searchRewards}
                  selectedReward={
                    (record?.rewardId && {
                      id: record.rewardId,
                      name: record.rewardName,
                    }) ||
                    null
                  }
                  disabled={!isEditable}
                  readOnly={params?.id ? true : false}
                  recordId={params?.id || null}
                  certificates={[
                    {
                      name: intl.formatMessage({ id: "no_certificate" }),
                      id: "no-cert",
                    },
                    ...certificates,
                  ]}
                  rewards={Array.isArray(rewards) ? rewards : []}
                />
                <Section
                  title="content_details"
                  titleValues={{
                    type: "",
                  }}
                >
                  <div className="grid">
                    <div className="col-12 md:col-6 lg:col-4">
                      <FileUpload
                        infoMessages={[
                          intl.formatMessage({
                            id: `upload_type_info_image`,
                          }),
                          intl.formatMessage(
                            {
                              id: `upload_file_dimensions`,
                            },
                            {
                              width: 1350,
                              height: 450,
                            }
                          ),
                        ]}
                        name="banner_file"
                        label="banner_image"
                        fileType={GlobalConstants.IMAGE_UPLOAD_FILE_TYPES}
                        isImage
                        fileUrl={
                          (record?.bannerImage &&
                            `${new URL(
                              record?.bannerImage,
                              process.env.REACT_APP_URL
                            ).toString()}`) ||
                          null
                        }
                        uploading={uploading?.[bannerFileName]}
                        progress={progress?.[bannerFileName] || 0}
                        onUpload={onBannerUpload}
                        readOnly={params?.id ? !isEditable : false}
                        uploadDetails={uploadDetails?.[bannerFileName]}
                        onClear={() => setBannerFileName("")}
                      />
                    </div>
                  </div>
                </Section>
              </div>
            </Form>
          )) ||
            null}
          {(activeTab === 1 && (
            <List
              selectionMode=""
              dataKey="learningMilestoneId"
              data={record?.milestones || []}
              columns={[
                {
                  field: "learningMilestone",
                  header: "learning_milestone",
                  sortable: false,
                  body: (rowData) => rowData?.learningMilestoneName || "",
                },
                {
                  field: "operator",
                  header: "learning_object_complusion",
                  sortable: false,
                  body: (rowData) =>
                    (rowData.order > 1 &&
                      intl.formatMessage({
                        id: rowData?.operator?.toLowerCase(),
                      })) ||
                    "-",
                },
              ]}
              pagination={false}
            />
          )) ||
            null}
          {(activeTab === 2 && (
            <List
              selectionMode=""
              dataKey="id"
              data={record?.assignments || []}
              columns={[
                {
                  field: "audience",
                  header: "assignment_on",
                  sortable: false,
                  body: (rowData) => {
                    return (
                      <>
                        <span>
                          {intl.formatMessage({
                            id: rowData?.subjectTypeName || "-",
                          })}
                        </span>
                        <span>
                          {" - "}
                          {rowData?.subjectName}
                        </span>
                      </>
                    );
                  },
                },
                {
                  field: "isOptional",
                  header: "training_type",
                  sortable: false,
                  body: ListCellRenders.TrainingType,
                },
                {
                  field: "startDate",
                  header: "start_date",
                  body: ListCellRenders.AssignmentStartDate,
                },
                {
                  field: "endDate",
                  header: "end_date",
                  body: ListCellRenders.AssignmentEndDate,
                },
                {
                  field: "relativeEndInDays",
                  header: "relative_end_days",
                  sortable: false,
                  body: (rowData) =>
                    rowData?.isOptional ? "-" : rowData?.relativeEndInDays,
                },
                {
                  field: "actions",
                  headerIcon: "pi-cog",
                  align: "center",
                  body: ListCellRenders.OtherActions([
                    {
                      label: "delete",
                      icon: "pi-trash",
                      isAllowed: canDelete,
                      action: (data) => onDeleteAction(data),
                    },
                  ]),
                },
              ]}
              pagination={false}
            />
          )) ||
            null}
        </div>
      </div>
      <Dialog
        style={{ width: "70vw", minWidth: "400px" }}
        visible={isAssignmentDialogVisible}
        onHide={() => setIsAssignmentDialogVisible(false)}
        header={intl.formatMessage({ id: "add_assignment" })}
      >
        <Form
          initialValues={{
            learningPathwayId: record?.id,
            subject_to: 1,
            is_optional: false,
            reenrol_if_completed: true,
            timezone: {
              id: Intl.DateTimeFormat().resolvedOptions().timeZone,
              name: Intl.DateTimeFormat().resolvedOptions().timeZone,
            },
          }}
          submitBtn={{
            label: intl.formatMessage({ id: "add_assignment" }),
            onClick: (values) => {
              handleAddAsignment(values);
              setIsAssignmentDialogVisible(false);
            },
          }}
          cancelBtn={{
            label: intl.formatMessage({ id: "cancel" }),
            onClick: () => setIsAssignmentDialogVisible(false),
          }}
        >
          <AssignmentDetailsForm
            audiences={audiences}
            onAudienceSearch={searchAudiences}
            progarms={progarms}
            recordId={record?.id}
            activeAssignments={record?.assignments?.map(
              (item) => item.subjectId
            )}
          />
        </Form>
      </Dialog>
      <ConfirmDialog />
    </div>
  );
};

const mapStateToProps = (state) => ({
  action: state.learningPathways?.action,
  progarms: state?.meta?.learningPrograms || [],
  audiences: state?.audiences?.data?.list || [],
  error: state.learningPathways?.error,
  learningMilestones: state?.learningMilestones?.data?.list || [],
  progress: state.uploads?.progress,
  record: state.learningPathways?.record,
  requesting: state.learningPathways?.requesting,
  uploadDetails: state.uploads?.details,
  uploading: state.uploads?.uploading,
  user: state.self?.user,
  certificates: state?.certificates?.data || [],
  rewards: state?.rewards?.data?.list || state?.rewards?.data || [],
});

const mapDispatchToProps = (dispatch) => ({
  resetUpload: () => dispatch(resetUpload()),
  uploadRequest: (data) => dispatch(uploadRequest(data)),
  createRequest: (data) => dispatch(createRequest(data)),
  detailsRequest: (id) => dispatch(detailsRequest(id)),
  updateDetailRequest: (data) => dispatch(updateDetailRequest(data)),
  milestoneListRequest: (data) => dispatch(milestoneListRequest(data)),
  learningProgramRequest: () => dispatch(learningProgramRequest()),
  assignmentsRequest: (data) => dispatch(assignmentsRequest(data)),
  audienceListRequest: (data) => dispatch(audienceListRequest(data)),
  addAssignmentRequest: (data) => dispatch(addAssignmentRequest(data)),
  certificateListRequest: (data) => dispatch(certificateListRequest(data)),
  rewardListRequest: (data) => dispatch(rewardListRequest(data)),
  deleteAssignmentRequest: (data) => dispatch(deleteAssignmentRequest(data)),
});

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(LearningPathwayDetails);
